import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ApiSuccessEnvelope,
  ChangeAffiliateEmailData,
  ChangeAffiliateEmailRequest,
} from "../../types/affiliates";
import { getPrivateInstance } from "../_privateInstance";

export type ChangeAffiliateEmailInput = {
  affiliateId: string;
  payload: ChangeAffiliateEmailRequest;
};

const changeAffiliateEmail = async ({
  affiliateId,
  payload,
}: ChangeAffiliateEmailInput): Promise<{
  data: ChangeAffiliateEmailData;
  message: string;
}> => {
  const path = API_ROUTES.CHANGE_AFFILIATE_EMAIL.replace(
    "{affiliate_id}",
    encodeURIComponent(affiliateId),
  );
  const response = await getPrivateInstance().patch<
    ApiSuccessEnvelope<ChangeAffiliateEmailData>
  >(path, payload);
  return response.data;
};

export const useChangeAffiliateEmail = (): UseMutationResult<
  { data: ChangeAffiliateEmailData; message: string },
  Error,
  ChangeAffiliateEmailInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["affiliates", "change-email"],
    mutationFn: changeAffiliateEmail,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["affiliates", "detail", variables.affiliateId],
      });
      queryClient.invalidateQueries({ queryKey: ["affiliates", "list"] });
    },
  });
};

export { changeAffiliateEmail };
